import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { PropsWithChildren } from "react";
import { JwtManager } from "../shared/JwtManager";
import { TokenHelper } from "../shared/TokenHelper";
import { useAuth0Context } from "./Auth0Context";
import { TokenApis } from "./TokenApis";

/**
 * High-level reason an Auth0/WorkUI auth flow entered an error state.
 *
 * - `auth0-callback-error`: Auth0 SDK reported a callback/session error.
 * - `login-redirect-failed`: Auth0 login redirect could not be started.
 * - `missing-auth0-id-token`: Auth0 session exists, but no raw ID token was available.
 * - `token-exchange-failed`: Auth0 ID token could not be exchanged for an app token.
 */
export type AuthErrorReason =
  | "auth0-callback-error"
  | "login-redirect-failed"
  | "missing-auth0-id-token"
  | "token-exchange-failed";

/**
 * Public auth/session state emitted by `AuthSessionOrchestratorProvider`.
 *
 * - `checking-session`: Auth0 SDK or the existing WorkUI token is resolving current session state.
 * - `signed-out`: no valid WorkUI session exists; use `mode` to choose Auth0 redirect or legacy login.
 * - `auth0-authenticated-awaiting-token`: Auth0 has a session, but no WorkUI token has been applied yet.
 * - `exchanging-token`: Auth0 ID token is being exchanged for a WorkUI token.
 * - `authenticated`: a valid WorkUI token exists; protected UI can render.
 * - `logging-out`: logout is in progress; login/token exchange should not start.
 * - `error`: auth flow reached a recoverable terminal error; inspect `reason`.
 */
export type AuthSessionStage =
  | { status: "checking-session" }
  | { status: "signed-out"; mode: "auth0" | "legacy" }
  | { status: "auth0-authenticated-awaiting-token" }
  | { status: "exchanging-token" }
  | { status: "authenticated" }
  | { status: "logging-out" }
  | { status: "error"; reason: AuthErrorReason; message?: string };

interface AuthSessionOrchestratorValue {
  stage: AuthSessionStage;
  /**
   * Starts the Auth0 redirect login flow when Auth0 is configured and the current
   * session state requires sign-in. Repeated calls for the same `returnTo` are ignored.
   */
  beginLogin: (returnTo: string) => Promise<void>;
  /**
   * Ends the current session. When Auth0 is configured, this also starts the
   * identity-provider logout flow; otherwise it performs local token cleanup.
   */
  endSession: () => Promise<void>;
}

interface AuthSessionOrchestratorProviderProps {
  onAuthenticated?: () => void;
  onSessionCleared?: () => void;
}

type AuthTokenExchangeError = {
  reason: Extract<AuthErrorReason, "missing-auth0-id-token" | "token-exchange-failed">;
  message?: string;
};

const DEFAULT_AUTH_SESSION_ORCHESTRATOR_VALUE: AuthSessionOrchestratorValue = {
  stage: { status: "signed-out", mode: "legacy" },
  beginLogin: async () => undefined,
  endSession: async () => undefined,
};

const AuthSessionOrchestratorContext = createContext<AuthSessionOrchestratorValue>(
  DEFAULT_AUTH_SESSION_ORCHESTRATOR_VALUE,
);
const tokenHelper = new TokenHelper({});
const tokenManager = new JwtManager({});

/**
 * Accessor for the local auth/session orchestration contract.
 *
 * Consumers should treat `stage` as the single source of truth for session
 * state and use the imperative methods only to trigger login/logout transitions.
 */
export function useAuthSessionOrchestrator(): AuthSessionOrchestratorValue {
  return useContext(AuthSessionOrchestratorContext);
}

/**
 * Provides the local auth/session orchestration layer.
 *
 * This provider derives the auth/session stage from Auth0 and WorkUI's existing
 * token storage, owns Auth0 callback token exchange, and coordinates logout.
 *
 * @param children React subtree that needs WorkUI auth/session access.
 * @param onAuthenticated Optional callback invoked after an Auth0 token is applied.
 * @param onSessionCleared Optional callback invoked after local token cleanup.
 * @returns A provider that supplies the local auth/session contract.
 */
export function AuthSessionOrchestratorProvider({
  children,
  onAuthenticated,
  onSessionCleared,
}: PropsWithChildren<AuthSessionOrchestratorProviderProps>) {
  const {
    getIdToken,
    isAuthenticated: isAuth0Authenticated,
    isAvailable,
    isAuthConfigLoading,
    isLoading,
    isLoggingOut,
    sdkErrorMessage,
    startLogin,
    logout,
  } = useAuth0Context();
  const [exchangeError, setExchangeError] = useState<AuthTokenExchangeError | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const [isLogoutRequested, setIsLogoutRequested] = useState(false);
  const loginRequestRef = useRef<string | null>(null);
  const exchangeRequestedRef = useRef(false);
  const logoutRequestedRef = useRef(false);
  const hasValidAppToken = tokenHelper.tokenIsValid();

  const stage = useMemo<AuthSessionStage>(() => {
    if (isLoggingOut || isLogoutRequested) {
      return { status: "logging-out" };
    }

    if (hasValidAppToken) {
      return { status: "authenticated" };
    }

    if (isAuthConfigLoading || isLoading) {
      return { status: "checking-session" };
    }

    if (isExchangingToken) {
      return { status: "exchanging-token" };
    }

    if (sdkErrorMessage) {
      return { status: "error", reason: "auth0-callback-error", message: sdkErrorMessage };
    }

    if (loginError) {
      return { status: "error", reason: "login-redirect-failed", message: loginError };
    }

    if (exchangeError) {
      return { status: "error", reason: exchangeError.reason, message: exchangeError.message };
    }

    if (isAuth0Authenticated) {
      return { status: "auth0-authenticated-awaiting-token" };
    }

    return { status: "signed-out", mode: isAvailable ? "auth0" : "legacy" };
  }, [
    exchangeError,
    hasValidAppToken,
    isAuth0Authenticated,
    isAuthConfigLoading,
    isAvailable,
    isExchangingToken,
    isLoading,
    loginError,
    isLogoutRequested,
    isLoggingOut,
    sdkErrorMessage,
  ]);

  useEffect(() => {
    if (!isAuth0Authenticated || isLoggingOut || isLogoutRequested || (!hasValidAppToken && !isExchangingToken)) {
      exchangeRequestedRef.current = false;
    }
  }, [hasValidAppToken, isAuth0Authenticated, isExchangingToken, isLoggingOut, isLogoutRequested]);

  // Exchange the Auth0 ID token once Auth0 is ready and no WorkUI session exists.
  useEffect(() => {
    if (
      isLoggingOut ||
      isLogoutRequested ||
      isAuthConfigLoading ||
      isLoading ||
      !isAvailable ||
      !isAuth0Authenticated ||
      hasValidAppToken ||
      sdkErrorMessage ||
      isExchangingToken ||
      exchangeError ||
      exchangeRequestedRef.current
    ) {
      return;
    }

    exchangeRequestedRef.current = true;
    setIsExchangingToken(true);

    void (async () => {
      try {
        const idToken = await getIdToken();
        if (!idToken) {
          setExchangeError({ reason: "missing-auth0-id-token", message: "Missing ID token." });
          return;
        }

        const authToken = await TokenApis.exchangeAuth0Token(idToken);
        tokenManager.storeAuthResponse(authToken.token, authToken.expires, authToken.refresh_token);
        setExchangeError(null);
        onAuthenticated?.();
      } catch (error) {
        setExchangeError({
          reason: "token-exchange-failed",
          message: error instanceof Error ? error.message : "Token exchange failed.",
        });
      } finally {
        setIsExchangingToken(false);
      }
    })();
  }, [
    exchangeError,
    getIdToken,
    hasValidAppToken,
    isAuth0Authenticated,
    isAuthConfigLoading,
    isAvailable,
    isExchangingToken,
    isLoading,
    isLoggingOut,
    isLogoutRequested,
    onAuthenticated,
    sdkErrorMessage,
  ]);

  useEffect(() => {
    if (stage.status === "signed-out" && stage.mode === "auth0") {
      return;
    }

    loginRequestRef.current = null;
  }, [stage]);

  const beginLogin = useCallback(
    async (returnTo: string) => {
      const canStartLogin = (stage.status === "signed-out" && stage.mode === "auth0") || stage.status === "error";

      if (isAuthConfigLoading || isLoading || isLoggingOut || isLogoutRequested || !isAvailable || !canStartLogin) {
        return;
      }

      if (loginRequestRef.current === returnTo) {
        return;
      }

      loginRequestRef.current = returnTo;
      setLoginError(null);

      try {
        await startLogin(returnTo);
      } catch (error) {
        loginRequestRef.current = null;
        setLoginError(error instanceof Error ? error.message : "Unable to start login.");
      }
    },
    [isAuthConfigLoading, isAvailable, isLoading, isLogoutRequested, isLoggingOut, stage, startLogin],
  );

  const endSession = useCallback(async () => {
    if (isLoggingOut || logoutRequestedRef.current) {
      return;
    }

    logoutRequestedRef.current = true;
    setIsLogoutRequested(true);
    exchangeRequestedRef.current = false;
    loginRequestRef.current = null;
    setExchangeError(null);
    setLoginError(null);
    tokenManager.removeToken();
    onSessionCleared?.();

    if (!isAvailable) {
      logoutRequestedRef.current = false;
      setIsLogoutRequested(false);
      return;
    }

    try {
      await logout();
    } catch (error) {
      logoutRequestedRef.current = false;
      setIsLogoutRequested(false);
      throw error;
    }
  }, [isAvailable, isLoggingOut, logout, onSessionCleared]);

  const value = useMemo<AuthSessionOrchestratorValue>(
    () => ({ stage, beginLogin, endSession }),
    [beginLogin, endSession, stage],
  );

  return <AuthSessionOrchestratorContext.Provider value={value}>{children}</AuthSessionOrchestratorContext.Provider>;
}
